<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{ width: '30vw' }"
    :header="translate(`${LOCALE_PREFIX}.deleteConfirmation.header`)"
    :draggable="false"
    :closable="false"
  >
    <Grid>
      <Col12>
        <div class="flex content-center">
          <div class="flex mr-4">
            <span class="material-symbols-rounded text-[2.5rem]">warning</span>
          </div>

          <div class="flex">
            <span>{{ translate(`${LOCALE_PREFIX}.deleteConfirmation.message`) }}</span>
          </div>
        </div>
      </Col12>

      <Col12>
        <span>{{ translate(`${LOCALE_PREFIX}.deleteConfirmation.deleteInstructions`) }}</span>
      </Col12>

      <Col12>
        <InputText
          :placeholder="translate(`${LOCALE_PREFIX}.deleteConfirmation.deletePlaceholder`)"
          v-model="deleteText"
        />
      </Col12>
    </Grid>

    <template #footer>
      <Button
        :label="translate(`${LOCALE_PREFIX}.deleteConfirmation.rejectLabel`)"
        text
        :disabled="deleting"
        @click="closeDialog"
      />
      <Button
        :label="translate(`${LOCALE_PREFIX}.deleteConfirmation.acceptLabel`)"
        severity="danger"
        :disabled="disableConfirm"
        :loading="deleting"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useTranslation } from '@centric-os/helpers';
import { computed, onUnmounted, ref } from 'vue';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { storeToRefs } from 'pinia';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import type { ApiResponseError } from '@centric-os/types';
import { useMealPlan } from '../composables';
import { SitesLocalePrefix } from '../../../../../enums';
import { useMealPlanStore } from '../../../../../routes/stores/use-meal-plan-store';
import { useSiteConfigStore } from '../../../../../routes';

// Set localization
const { translate } = useTranslation();
const LOCALE_PREFIX = SitesLocalePrefix.SITE_MEAL_PLAN;

// Ref for delete text
const deleteText = ref<string>('');

// Define visible ref
const visible = defineModel({
  type: Boolean,
  required: true,
});

// Define props
const props = defineProps({
  isSiteConfig: {
    type: Boolean,
    required: true,
  },
});

// Set global toast
const { showErrorToast, showSuccessToast } = useGlobalToast();

// Set site store
const siteStore = useSiteStore();
const { site } = storeToRefs(siteStore);

// Set meal plan store
const mealPlanStore = useMealPlanStore();
const { loading: deleting } = useStoreAPIAction(mealPlanStore, 'saveMealPlan');

// Set meal plan composable
const { mapDeletePrivateConfigPayload, mapDeletePublicConfigPayload } = useMealPlan();

// Method to close dialog
const closeDialog = (): void => {
  deleteText.value = null;
  visible.value = false;
};

// Method to confirm delete
const confirm = async (): Promise<void> => {
  const privatePayload = mapDeletePrivateConfigPayload();
  const publicPayload = mapDeletePublicConfigPayload();

  await mealPlanStore
    .saveMealPlan(site.value.id as string, props.isSiteConfig, privatePayload, publicPayload)
    .then(async () => {
      if (props.isSiteConfig) {
        const siteConfigStore = useSiteConfigStore();
        await siteConfigStore.fetchSiteConfigurations(site.value.id);
      }
      showSuccessToast(translate(`${LOCALE_PREFIX}.deleteConfirmation.success`));
      closeDialog();
    })
    .catch((error: ApiResponseError) => {
      if (error.status) {
        if (error.status >= 400 && error.status < 500) {
          showErrorToast(error?.error ? error?.error : (error?.message as string));
        } else {
          showErrorToast(translate(`${LOCALE_PREFIX}.deleteConfirmation.error`));
        }
      }
    });
};

// Computed property to disable confirm button
const disableConfirm = computed<boolean>(() => {
  return deleteText.value !== 'REMOVE';
});

onUnmounted(() => {
  deleteText.value = null;
});
</script>
